import { forwardRef, useLayoutEffect, useRef, type KeyboardEvent, type ReactNode, type Ref } from 'react';

import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import {
  composerMentionDisplayToken,
  deleteComposerMentionAtCaret,
  displayComposerText,
  reconcileComposerSegmentsFromDisplay,
  type MartinaComposerSegment,
} from '@/components/exai/martinaComposerMentionContract';

type Props = {
  segments: MartinaComposerSegment[];
  onSegmentsChange: (next: MartinaComposerSegment[]) => void;
  /** Caret nel display unico: serve al picker `@` e all'inserimento menzione. */
  onCaretChange?: (caret: number) => void;
  id?: string;
  placeholder?: string;
  ariaLabel?: string;
  className?: string;
  textareaClassName?: string;
  rows?: number;
  readOnly?: boolean;
  interimSuffix?: string;
  onKeyDown?: (event: KeyboardEvent<HTMLTextAreaElement>) => void;
  footer?: ReactNode;
};

function assignRef<T>(ref: Ref<T> | undefined, value: T | null) {
  if (!ref) return;
  if (typeof ref === 'function') {
    ref(value);
    return;
  }
  (ref as { current: T | null }).current = value;
}

/**
 * Composer social-style: un solo textarea, menzioni `@Nome` inline nel testo.
 * Niente chip grafici / X. Backspace/Delete a bordo token rimuovono tutta la
 * menzione. Il payload Martina resta `serializeComposerPayload(segments)`.
 */
export const MartinaComposerInlineField = forwardRef(function MartinaComposerInlineField(
  {
    segments,
    onSegmentsChange,
    onCaretChange,
    id,
    placeholder,
    ariaLabel,
    className,
    textareaClassName,
    rows = 2,
    readOnly = false,
    interimSuffix = '',
    onKeyDown,
    footer,
  }: Props,
  ref: Ref<HTMLTextAreaElement>,
) {
  const localRef = useRef<HTMLTextAreaElement | null>(null);
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const pendingCaretRef = useRef<number | null>(null);
  const display = displayComposerText(segments);
  const value = interimSuffix ? [display, interimSuffix].filter(Boolean).join(' ') : display;

  useLayoutEffect(() => {
    const node = localRef.current;
    if (!node) return;
    const resize = () => {
      // Ricalcola anche quando cambia la larghezza: il wrapping mobile puo'
      // aggiungere righe senza cambiare il valore della textarea.
      node.style.height = 'auto';
      node.style.height = `${Math.min(node.scrollHeight, 256)}px`;
      if (overlayRef.current) overlayRef.current.scrollTop = node.scrollTop;
    };
    resize();
    let width = node.clientWidth;
    let frame: number | null = null;
    const observer = new ResizeObserver(() => {
      if (node.clientWidth === width) return;
      width = node.clientWidth;
      // La misura cambia fuori dal callback dell'observer, evitando il loop
      // ResizeObserver quando il nuovo wrapping cambia anche l'altezza.
      if (frame !== null) window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        frame = null;
        resize();
      });
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
      if (frame !== null) window.cancelAnimationFrame(frame);
    };
  }, [value, rows]);

  useLayoutEffect(() => {
    const node = localRef.current;
    const caret = pendingCaretRef.current;
    if (!node || caret == null) return;
    node.setSelectionRange(caret, caret);
    pendingCaretRef.current = null;
    onCaretChange?.(caret);
  }, [segments, onCaretChange]);

  const reportCaret = (node: HTMLTextAreaElement | null) => {
    if (!node) return;
    onCaretChange?.(node.selectionStart ?? node.value.length);
  };

  return (
    <div className={cn('relative rounded-lg border border-input bg-background', className)}>
      <div
        ref={overlayRef}
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 overflow-hidden whitespace-pre-wrap break-words px-3 py-2 text-sm leading-6"
      >
        {value ? segments.map((segment, index) => segment.kind === 'text'
          ? <span key={`text-${index}`}>{segment.value}</span>
          : <a key={`${segment.anchor.entityRef}-${index}`} href={segment.href} data-crm-inline-reference={segment.anchor.entityRef} className="font-semibold text-primary underline decoration-2 underline-offset-2">{composerMentionDisplayToken(segment.anchor.title)}</a>)
          : <span className="text-muted-foreground">{placeholder}</span>}
        {interimSuffix && <span>{display ? ' ' : ''}{interimSuffix}</span>}
      </div>
      <Textarea
        ref={(node) => {
          localRef.current = node;
          assignRef(ref, node);
        }}
        value={value}
        readOnly={readOnly}
        onChange={(event) => {
          // Durante la dettatura il campo è readOnly: interimSuffix non entra nei segmenti.
          const nextDisplay = event.target.value;
          const known = segments.filter(
            (segment): segment is Extract<MartinaComposerSegment, { kind: 'chip' }> => segment.kind === 'chip',
          );
          onSegmentsChange(reconcileComposerSegmentsFromDisplay(known, nextDisplay));
          onCaretChange?.(event.target.selectionStart ?? nextDisplay.length);
        }}
        onSelect={(event) => reportCaret(event.currentTarget)}
        onClick={(event) => reportCaret(event.currentTarget)}
        onKeyUp={(event) => reportCaret(event.currentTarget)}
        onScroll={(event) => {
          if (overlayRef.current) overlayRef.current.scrollTop = event.currentTarget.scrollTop;
        }}
        rows={rows}
        id={id}
        placeholder={placeholder}
        aria-label={ariaLabel}
        onKeyDown={(event) => {
          if (!readOnly && (event.key === 'Backspace' || event.key === 'Delete')) {
            const node = event.currentTarget;
            const start = node.selectionStart ?? 0;
            const end = node.selectionEnd ?? start;
            if (start === end) {
              const removed = deleteComposerMentionAtCaret(
                segments,
                start,
                event.key === 'Backspace' ? 'backspace' : 'delete',
              );
              if (removed) {
                event.preventDefault();
                pendingCaretRef.current = removed.caret;
                onSegmentsChange(removed.segments);
                onCaretChange?.(removed.caret);
                return;
              }
            }
          }
          onKeyDown?.(event);
        }}
        className={cn(
          'relative min-h-[2.75rem] w-full resize-none border-0 bg-transparent px-3 py-2 text-sm leading-6 text-transparent caret-foreground placeholder:text-transparent shadow-none focus-visible:ring-0',
          textareaClassName,
        )}
      />
      {footer}
    </div>
  );
});
